"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { tokens } from "@/lib/design-tokens";

// Define all possible button variants using class-variance-authority
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm",
        destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm",
        outline: "border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white",
        secondary: "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700",
        ghost: "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-white",
        link: "text-blue-600 dark:text-blue-400 underline-offset-4 hover:underline",
        white: "bg-white text-blue-600 hover:bg-white/90 border border-white/10",
        success: "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-sm",
        warning: "bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8 text-base",
        icon: "h-10 w-10 rounded-full",
        iconSm: "h-8 w-8 rounded-full",
        iconLg: "h-12 w-12 rounded-full",
        wide: "h-10 px-8 py-2",
      },
      isLoading: {
        true: "relative text-transparent transition-none hover:text-transparent",
        false: "",
      },
      isFullWidth: {
        true: "w-full",
        false: "",
      },
      hasRipple: {
        true: "overflow-hidden relative",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      isLoading: false,
      isFullWidth: false,
      hasRipple: true,
    },
  }
);

// Define the props for our enhanced button
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  isFullWidth?: boolean;
  hasRipple?: boolean;
  rippleColor?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// Create the Button component with all our enhancements
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, variant, size, isLoading, loadingText, isFullWidth, hasRipple, rippleColor, leftIcon, rightIcon, ...props }, ref) => {
    const rippleRef = React.useRef<HTMLSpanElement>(null);
    
    // Handle ripple effect on click
    const handleRipple = hasRipple ? (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!rippleRef.current) return;
      
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      
      // Calculate ripple position
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      // Set ripple position and start animation
      const ripple = rippleRef.current;
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      ripple.style.backgroundColor = rippleColor || "rgba(255,255,255,0.3)";
      
      ripple.classList.remove("animate-ripple");
      // Trigger reflow to restart animation
      void ripple.offsetWidth;
      ripple.classList.add("animate-ripple");
    } : undefined;
    
    return (
      <button
        className={cn(buttonVariants({ variant, size, isLoading, isFullWidth, hasRipple, className }))}
        ref={ref}
        onClick={handleRipple}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
        
        <span className="flex items-center gap-2">
          {leftIcon && <span>{leftIcon}</span>}
          {children}
          {rightIcon && <span>{rightIcon}</span>}
        </span>
        
        {hasRipple && <span ref={rippleRef} className="absolute rounded-full scale-0 opacity-50" />}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };